import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Policies.css';

const Policies: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activePolicy, setActivePolicy] = useState<'shipping' | 'exchanges' | 'faqs'>('shipping');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    const currentPath = window.location.pathname;
    
    if (tab === 'shipping' || tab === 'exchanges' || tab === 'faqs') {
      setActivePolicy(tab);
    } else if (currentPath === '/shipping') {
      setActivePolicy('shipping');
    } else if (currentPath === '/exchanges') {
      setActivePolicy('exchanges');
    } else if (currentPath === '/faqs') {
      setActivePolicy('faqs');
    }
    
  }, [searchParams]);

  return (
    <div className="policies">
      <div className="policies-header">
        <div className="policy-tabs">
          <button 
            className={`policy-tab ${activePolicy === 'shipping' ? 'active' : ''}`}
            onClick={() => setActivePolicy('shipping')}
          >
            <span>SHIPPING POLICY</span>
          </button>
          <button 
            className={`policy-tab ${activePolicy === 'faqs' ? 'active' : ''}`}
            onClick={() => setActivePolicy('faqs')}
          >
            <span>FAQs</span>
          </button>
          <button 
            className={`policy-tab ${activePolicy === 'exchanges' ? 'active' : ''}`}
            onClick={() => setActivePolicy('exchanges')}
          >
            <span>EXCHANGES POLICY</span>
          </button>
        </div>
      </div>

        <div className="policy-content">
          {activePolicy === 'shipping' && (
            <div className="policy-info">
              <div className="policy-header">
                <h1>SHIPPING POLICY</h1>
              </div>
              <section className="policy-section">
                <h2>SHIPPING SCHEDULE</h2>
                <p>Orders are processed and dispatched within 2–7 business days after your purchase.</p>
                <p>Once your order has been shipped, you'll receive a confirmation email with a tracking number, allowing you to follow your package every step of the way.</p>
              </section>

              <section className="policy-section">
                <h2>SHIPPING COSTS & DELIVERY TIMES</h2>
                
                <div className="shipping-option">
                  <h3>GREECE</h3>
                  <div className="shipping-details">
                    <p><strong>Shipping Options:</strong> Box Now / ELTA</p>
                    <p><strong>Cost:</strong> €3</p>
                    <p><strong>Estimated Delivery Time:</strong> 3–5 business days</p>
                  </div>
                </div>

                <div className="shipping-option">
                  <h3>CYPRUS</h3>
                  <div className="shipping-details">
                    <p><strong>Shipping Options:</strong> Box Now / ELTA</p>
                    <p><strong>Cost:</strong> €7 (Box Now), €10 (ELTA)</p>
                    <p><strong>Estimated Delivery Time:</strong> 3–7 business days</p>
                  </div>
                </div>

                <div className="shipping-option">
                  <h3>EUROPE & USA</h3>
                  <div className="shipping-details">
                    <p><strong>Shipping Option:</strong> ELTA</p>
                    <p><strong>Cost:</strong> €15 (up to 1 kg), €30 (over 1 kg)</p>
                    <p><strong>Estimated Delivery Time:</strong> Up to 14 business days</p>
                  </div>
                </div>

                <div className="shipping-option">
                  <h3>REST OF THE WORLD</h3>
                  <div className="shipping-details">
                    <p><strong>Shipping Option:</strong> ELTA</p>
                    <p><strong>Cost:</strong> €15 (up to 1 kg), €30 (over 1 kg)</p>
                    <p><strong>Estimated Delivery Time:</strong> Up to 14 business days (may vary by location)</p>
                  </div>
                </div>
              </section>

              <section className="policy-section">
                <h2>FREE SHIPPING OFFERS</h2>
                <ul className="free-shipping-list">
                  <li><strong>GREECE:</strong> Free shipping on orders over €50</li>
                  <li><strong>CYPRUS:</strong> Free shipping on orders over €80</li>
                </ul>
                <p>Free shipping is automatically applied at checkout once your order qualifies.</p>
              </section>

              <section className="policy-section">
                <h2>COURIER INFORMATION</h2>
                <p>We currently offer two delivery methods for Greece and Cyprus: Box Now and ELTA. Both are trusted providers, select the option that works best for your needs.</p>
                <p>For international deliveries, we ship exclusively via ELTA.</p>
              </section>

              <section className="policy-section">
                <h2>IMPORTANT NOTES</h2>
                <ul className="important-notes">
                  <li>Business days refer to weekdays (Monday–Friday) and exclude weekends and public holidays (αργίες).</li>
                  <li>Delivery times are estimated and may vary depending on your location and local courier conditions.</li>
                  <li>Delays may occur during holiday seasons, sales, or due to customs clearance procedures.</li>
                  <li>For international orders, additional customs duties or taxes may apply. These are the customer's responsibility and are not included in the shipping cost.</li>
                  <li>Please note that order processing time is separate from delivery time.</li>
                </ul>
              </section>
            </div>
          )}

          {activePolicy === 'exchanges' && (
            <div className="policy-info">
              <div className="policy-header">
                <h1>EXCHANGES POLICY</h1>
              </div>
              <section className="policy-section">
                <h2>EXCHANGE PERIOD</h2>
                <ul className="exchange-list">
                  <li>Exchanges must be requested within 10 business days from the date you received your order.</li>
                  <li>After this period, we unfortunately cannot process your request.</li>
                </ul>
              </section>

              <section className="policy-section">
                <h2>HOW TO EXCHANGE</h2>
                <p>You can exchange your item in two ways:</p>
                
                <div className="exchange-method">
                  <h3>1. Via Shipping</h3>
                  <ul className="method-details">
                    <li>You may send the item back to us for exchange.</li>
                    <li>Please note: All shipping costs (both ways) are the customer's responsibility.</li>
                    <li>We recommend using a tracked service, as we are not responsible for lost or damaged parcels.</li>
                  </ul>
                </div>

                <div className="exchange-method">
                  <h3>2. In-Store</h3>
                  <ul className="method-details">
                    <li>You're welcome to exchange your item in person at our physical store within the 10 business day window.</li>
                    <li>Please bring your original receipt or order confirmation email.</li>
                  </ul>
                </div>
              </section>

              <section className="policy-section">
                <h2>ELIGIBILITY FOR EXCHANGE</h2>
                <p>To be eligible for an exchange:</p>
                <ul className="eligibility-list">
                  <li>Items must be in their original condition – unworn, unwashed, and with all original tags and packaging intact.</li>
                  <li>We reserve the right to refuse exchanges that do not meet these criteria.</li>
                </ul>
              </section>

              <section className="policy-section">
                <h2>PLEASE NOTE</h2>
                <ul className="note-list">
                  <li>No cash or credit card refunds are issued.</li>
                  <li>We do not cover any customs fees, shipping delays, or lost return parcels.</li>
                  <li>Sale items, limited edition pieces, or personalized/custom items may not be eligible for exchange.</li>
                </ul>
              </section>
            </div>
          )}

          {activePolicy === 'faqs' && (
            <div className="policy-info">
              <div className="policy-header">
                <h1>FAQs</h1>
                <p>Got questions? We've got you ♡</p>
              </div>

              <div className="faqs-content">
                <div className={`faq-item ${openFAQ === 0 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(0)}>
                    <h3>✮ When will my order ship?</h3>
                    <span className="faq-toggle">{openFAQ === 0 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>Orders are usually processed and shipped within 2 to 7 business days, and you'll receive a confirmation email with tracking as soon as your package is on its way.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 1 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(1)}>
                    <h3>✮ How much is shipping?</h3>
                    <span className="faq-toggle">{openFAQ === 1 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <ul>
                      <li><strong>Greece:</strong> €3 (Box Now or ELTA), delivery in 3 to 5 business days, free shipping on orders over €50</li>
                      <li><strong>Cyprus:</strong> €7 (Box Now), €10 (ELTA), delivery in 3 to 7 business days, free shipping on orders over €80</li>
                      <li><strong>Europe and USA:</strong> From €15 (up to 0.5kg) to €30 (over 1kg), approx. 14 business days</li>
                      <li><strong>Rest of the world:</strong> Same as above, delivery time may vary depending on customs</li>
                    </ul>
                    <p><a href="/shipping">Full details on our Shipping Policy</a></p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 2 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(2)}>
                    <h3>✮ Do you accept returns?</h3>
                    <span className="faq-toggle">{openFAQ === 2 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>We currently do not accept returns, but we do offer exchanges within 10 business days of delivery, either by shipping the item back (customer covers shipping) or by visiting our physical store in Thessaloniki.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 3 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(3)}>
                    <h3>✮ How do I exchange an item?</h3>
                    <span className="faq-toggle">{openFAQ === 3 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>Email us at <a href="mailto:eco.fashion.living@gmail.com">eco.fashion.living@gmail.com</a> or DM us on Instagram <a href="https://instagram.com/eco.fashion.living" target="_blank" rel="noopener noreferrer">@eco.fashion.living</a> with your order number, and we'll guide you through the process.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 4 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(4)}>
                    <h3>✮ Are your vintage pieces authentic?</h3>
                    <span className="faq-toggle">{openFAQ === 4 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>Yes, all branded and designer pieces are authentically sourced and carefully inspected. We also carry deadstock vintage and reworked designs, all curated and created in-house.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 5 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(5)}>
                    <h3>✮ Are your items one of a kind?</h3>
                    <span className="faq-toggle">{openFAQ === 5 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>Most of our pieces are one of a kind, carefully selected for their uniqueness. However, some items come from old boutiques and may have a small stock available, which is usually very limited and rare.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 6 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(6)}>
                    <h3>✮ Do you restock items?</h3>
                    <span className="faq-toggle">{openFAQ === 6 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>Because many items are unique, we generally do not restock exact pieces. But we are always sourcing fresh vintage and deadstock gems, so keep an eye out for new arrivals.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 7 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(7)}>
                    <h3>✮ Can I reserve an item?</h3>
                    <span className="faq-toggle">{openFAQ === 7 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>Currently, we do not offer reservations. Items are sold on a first-come, first-served basis.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 8 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(8)}>
                    <h3>✮ How can I be sure the sunglasses are legit?</h3>
                    <span className="faq-toggle">{openFAQ === 8 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>We understand there are many knock-offs on the market, so this concern is completely normal. All our eyewear includes a serial number on the frame, which you can verify online. Most of the time, you will find matching or very similar listings that confirm their authenticity.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 9 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(9)}>
                    <h3>✮ Can I customize the lenses in your branded and designer glasses?</h3>
                    <span className="faq-toggle">{openFAQ === 9 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>Yes, our glasses come with demo (non-prescription) lenses. You can easily take the frames to your optician to fit them with prescription lenses or tinted shades, whichever suits your style and needs.</p>
                  </div>
                </div>

                <div className={`faq-item ${openFAQ === 10 ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFAQ(10)}>
                    <h3>✮ Where is your store located?</h3>
                    <span className="faq-toggle">{openFAQ === 10 ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>We are in Thessaloniki city center, right across from Agora Modiano, just one minute from Aristotelous Square. You can find us on the 5th floor of a hidden arcade (στοά).</p>
                    <p>Need help finding us? Check the directions on our <a href="/space">Visit Our Space</a> page.</p>
                  </div>
                </div>

                <div className="faq-item final-cta">
                  <h3>✮ Still have questions? ✮</h3>
                  <p>We're here for you!</p>
                  <p>Feel free to reach out at <a href="mailto:eco.fashion.living@gmail.com">eco.fashion.living@gmail.com</a> or DM us on Instagram <a href="https://instagram.com/eco.fashion.living" target="_blank" rel="noopener noreferrer">@eco.fashion.living</a> we're always happy to help ♡</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {(activePolicy === 'shipping' || activePolicy === 'exchanges') && (
          <div className="faq-item final-cta">
            <h3>✮ Questions? ✮</h3>
            <p>We're here for you!</p>
            <p>Feel free to reach out at <a href="mailto:eco.fashion.living@gmail.com">eco.fashion.living@gmail.com</a> or DM us on Instagram <a href="https://instagram.com/eco.fashion.living" target="_blank" rel="noopener noreferrer">@eco.fashion.living</a> we're always happy to help ♡</p>
          </div>
        )}

        {activePolicy === 'faqs' && (
          <div className="policy-contact">
            <h2>QUESTIONS?</h2>
            <p>We're here to help!</p>
            <p>Contact us at <a href="mailto:eco.fashion.living@gmail.com">eco.fashion.living@gmail.com</a> or DM us on Instagram <a href="https://instagram.com/eco.fashion.living" target="_blank" rel="noopener noreferrer">@eco.fashion.living</a> ♡</p>
          </div>
        )}
    </div>
  );
};

export default Policies;